import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { Location, TripAttributesGps } from '../dto/visit-gps.interface';

/**
 * Status Indicator Cell Renderer
 *
 * Muestra íconos de movimiento (flag) y batería con colores según estado.
 *
 * @example
 * ```typescript
 * {
 *   field: 'status',
 *   headerName: 'Indicadores',
 *   cellRenderer: StatusIndicatorCellRendererComponent,
 *   minWidth: 120
 * }
 * ```
 */
@Component({
  selector: 'app-status-icons-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="flex items-center justify-center space-x-2">
      <!-- FLAG ICON -->
      <mat-icon
        [style.color]="getColorGPS(params.data?.locations)"
        [matTooltip]="isMoving(params.data?.locations) ? 'En movimiento' : 'Detenido'"
        svgIcon="heroicons_solid:flag"
      ></mat-icon>

      <!-- BATTERY ICON -->
      <ng-container *ngIf="getBattery(params.data?.locations) as battery">
        <mat-icon
          *ngIf="battery.percent > 70"
          class="text-green-600"
          matTooltip="{{ battery.text }}"
          >battery_full</mat-icon
        >

        <mat-icon
          *ngIf="battery.percent <= 70 && battery.percent > 30"
          class="text-yellow-500"
          matTooltip="{{ battery.text }}"
          >battery_charging_full</mat-icon
        >

        <mat-icon
          *ngIf="battery.percent <= 30"
          class="text-red-600"
          matTooltip="{{ battery.text }}"
          >battery_alert</mat-icon
        >
      </ng-container>
    </div>
  `,
})
export class StatusIndicatorCellRendererComponent implements CellRenderer<TripAttributesGps> {
  @Input() params!: CellRendererParams<TripAttributesGps>;

  /**
   * Retorna `true` si se está moviendo, `false` si no.
   */
  isMoving(locations: Location[]): boolean {
    if (!locations || locations.length === 0) return false;
    const latest = locations.reduce((prev, curr) =>
      new Date(prev.timestamp) > new Date(curr.timestamp) ? prev : curr
    );
    return latest.is_moving !== false;
  }

  /**
   * Retorna el color del ícono de GPS (bandera).
   */
  getColorGPS(locations: Location[]): string {
    if (!locations || locations.length === 0) return '#9ca3af'; // gris
    return this.isMoving(locations) ? '#22c55e' : '#ef4444'; // verde o rojo
  }

  /**
   * Obtiene el nivel de batería desde `locations[0].battery.level`
  */
  getBattery(locations: Location[]): { percent: number; text: string } {
    if (!locations || locations.length === 0) return { percent: 0, text: '0%' };
    const latest = locations.reduce((prev, curr) =>
      new Date(prev.timestamp) > new Date(curr.timestamp) ? prev : curr
    );

    const level = latest.battery?.level ?? 0;
    const percent = Math.round(level * 100);
    return { percent, text: `${percent}%` };
  }
}
